import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { formatSeconds } from '../../av/time';
import { sessionDuration } from '../../domain/avOps';
import type { AvSession, Project } from '../../domain/schemas';
import { debugCounters } from '../../lib/debug';
import { useObservable } from '../../lib/useObservable';
import { SessionFrameRenderer } from '../../video/frameRenderer';
import { renderSessionAudio } from '../../video/sessionAudio';
import type { RenderAudio } from '../../audio/render';
import type { AudioFeatures } from '../../visualizer/analysis';
import { takeSnapshot } from '../../video/snapshot';
import { useServices } from '../../state/services';
import { codecsFor, defaultMethod, detectCapabilities, formatLabel, type ExportCapabilities } from '../../video/capabilities';
import {
  AUDIO_SAMPLE_RATE,
  DEFAULT_VIDEO_SETTINGS,
  MAX_VIDEO_BYTES,
  VIDEO_FPS,
  VIDEO_RESOLUTIONS,
  checkRange,
  estimateExport,
  sanitizeFileName,
  type ExportMethod,
  type VideoCodec,
  type VideoFps,
  type VideoQuality,
  type VideoResolution,
  type VideoSettings,
} from '../../video/frames';
import { PHASE_LABELS } from '../../video/videoExporter';
import { Alert, Button, inputClass } from '../ui';

const METHOD_LABELS: Record<ExportMethod, string> = {
  offline: 'Offline · frame-accurate',
  realtime: 'Real-time recording · not frame-accurate',
};

const mb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;

/**
 * Video export of the selected sync session (Phase 4B). Offers only what this browser's
 * encoders confirm; the export path in use is always named. While exporting, the preview is
 * paused and the export renders in its own off-page renderer.
 */
export function VideoExportPanel({ project, session }: { project: Project; session: AvSession }) {
  const { videoExporter, sync, images, buffers, analysis } = useServices();
  const state = useObservable(videoExporter.state);
  const [caps, setCaps] = useState<ExportCapabilities | null>(null);
  const [capsError, setCapsError] = useState<string | null>(null);
  const [resolution, setResolution] = useState<VideoResolution>(DEFAULT_VIDEO_SETTINGS.resolution);
  const [fps, setFps] = useState<VideoFps>(DEFAULT_VIDEO_SETTINGS.fps);
  const [quality, setQuality] = useState<VideoQuality>(DEFAULT_VIDEO_SETTINGS.quality);
  const [codec, setCodec] = useState<VideoCodec>(DEFAULT_VIDEO_SETTINGS.codec);
  const [method, setMethod] = useState<ExportMethod | null>(null);
  const [includeAudio, setIncludeAudio] = useState(DEFAULT_VIDEO_SETTINGS.includeAudio);
  const [useRange, setUseRange] = useState(false);
  const [start, setStart] = useState('0');
  const [end, setEnd] = useState('');
  const [name, setName] = useState(session.name);
  // The file name follows the selected session (switching sessions resets it).
  const [nameSession, setNameSession] = useState(session.id);
  if (nameSession !== session.id) {
    setNameSession(session.id);
    setName(session.name);
  }

  useEffect(() => {
    let live = true;
    detectCapabilities().then(
      (c) => {
        if (!live) return;
        setCaps(c);
        setMethod((m) => m ?? defaultMethod(c));
      },
      (e: unknown) => live && setCapsError(e instanceof Error ? e.message : String(e)),
    );
    return () => {
      live = false;
    };
  }, []);
  // Leaving the Sync workspace (or the session) cancels a running export.
  useEffect(() => () => videoExporter.cancel(), [videoExporter, session.id]);
  // Test hook: the deterministic frame renderer at given session positions (downsampled pixels).
  const latest = useRef({ project, session });
  useLayoutEffect(() => {
    latest.current = { project, session };
  });
  useEffect(() => {
    // Test hooks: compiled out of the production build (see vite.config.ts). The frame and
    // audio probes (Phase 4B) joined the Phase 5B.0 hooks in Phase 5C.1: only the browser
    // tests use them, and they render frames and audio on request.
    if (__SONICFORGE_TEST_HOOKS__) {
      debugCounters.videoFrameProbe = (seconds, width, height) => probeFrames(latest.current.project, latest.current.session.id, seconds, width, height, images.load, analysis.peek(latest.current.project, latest.current.session));
      debugCounters.videoAudioProbe = (start, seconds) => probeAudio(latest.current.project, latest.current.session, start, seconds, buffers);
      const renderer = (width: number, height: number) =>
        SessionFrameRenderer.create(takeSnapshot(latest.current.project, latest.current.session.id), width, height, { loadImage: images.load, features: analysis.peek(latest.current.project, latest.current.session) });
      debugCounters.videoFrameCapture = async (seconds, width, height) => withRenderer(await renderer(width, height), (r) => seconds.map((t) => (r.renderFrame(t), r.canvas.toDataURL('image/png'))));
      debugCounters.videoFrameBench = async (seconds, width, height) => {
        const t0 = performance.now();
        const r = await renderer(width, height);
        const createMs = performance.now() - t0;
        return withRenderer(r, () => ({ createMs, frameMs: seconds.map((t) => timedFrame(r, t)) }));
      };
    }
    return () => {
      if (__SONICFORGE_TEST_HOOKS__) {
        debugCounters.videoFrameProbe = undefined;
        debugCounters.videoAudioProbe = undefined;
        debugCounters.videoFrameCapture = undefined;
        debugCounters.videoFrameBench = undefined;
      }
    };
  }, [images, buffers, analysis]);

  const sessionSec = sessionDuration(project, session);
  const codecs = caps && method ? codecsFor(caps, method, resolution) : [];
  const chosenCodec = codecs.includes(codec) ? codec : codecs[0];
  const range = useRange ? { start: Number(start), end: end.trim() === '' ? sessionSec : Number(end) } : null;
  const rangeCheck = checkRange(range, sessionSec, fps);
  const estimate = estimateExport({ resolution, fps, quality, includeAudio }, rangeCheck.ok ? rangeCheck.end - rangeCheck.start : 0);
  const tooBig = estimate.bytes > MAX_VIDEO_BYTES;
  const running = state.status === 'running';
  const canStart = !!caps && !!method && !!chosenCodec && rangeCheck.ok && !tooBig && !running;
  const fileName = sanitizeFileName(name, sanitizeFileName(session.name).replace(/\.webm$/, ''));

  const startExport = () => {
    if (!canStart || !method || !chosenCodec) return;
    // The preview pauses: the export has its own renderer and must not compete for audio or CPU.
    sync.pause();
    const settings: VideoSettings = { resolution, fps, codec: chosenCodec, quality, method, range: rangeCheck.ok && useRange ? { start: rangeCheck.start, end: rangeCheck.end } : null, includeAudio, fileName };
    void videoExporter.export(project, session.id, settings).catch(() => undefined);
  };

  const unsupported = caps && !caps.offline.available && !caps.realtime.available;
  return (
    <div className="flex flex-col gap-3" data-testid="video-export">
      <div
        className="text-xs text-zinc-400"
        data-testid="video-caps"
        data-status={caps ? (unsupported ? 'unsupported' : 'ready') : capsError ? 'error' : 'checking'}
        data-offline={caps?.offline.available ? 'yes' : 'no'}
        data-realtime={caps?.realtime.available ? 'yes' : 'no'}
        data-formats={caps ? [...new Set([...codecsFor(caps, 'offline', resolution), ...codecsFor(caps, 'realtime', resolution)])].join(',') : ''}
      >
        {!caps && !capsError && 'Checking this browser’s video encoders…'}
        {capsError && `Could not check video support: ${capsError}`}
        {caps && unsupported && (
          <Alert title="Video export is not available in this browser">
            Missing: {[...new Set([...caps.offline.missing, ...caps.realtime.missing])].join(', ')}. Try a current Chrome or Edge. WAV export (Music tab) still works.
          </Alert>
        )}
        {caps && !unsupported && (
          <p>
            Export path: <strong className="text-zinc-200" data-testid="video-path">{method ? METHOD_LABELS[method] : '—'}</strong>
            {caps.offline.available ? ' · WebCodecs encoders available' : ' · frame-accurate export unavailable here (missing ' + caps.offline.missing.join(', ') + ')'}
          </p>
        )}
      </div>
      {caps && !unsupported && (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Method
              <select className={inputClass} value={method ?? ''} onChange={(e) => setMethod(e.target.value as ExportMethod)} disabled={running} data-testid="video-method">
                {caps.offline.available && <option value="offline">{METHOD_LABELS.offline}</option>}
                {caps.realtime.available && <option value="realtime">{METHOD_LABELS.realtime}</option>}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Format
              <select className={inputClass} value={chosenCodec ?? ''} onChange={(e) => setCodec(e.target.value as VideoCodec)} disabled={running || codecs.length === 0} data-testid="video-format">
                {codecs.map((c) => (
                  <option key={c} value={c}>
                    {formatLabel(c)}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Resolution
              <select className={inputClass} value={resolution} onChange={(e) => setResolution(e.target.value as VideoResolution)} disabled={running} data-testid="video-resolution">
                {(Object.keys(VIDEO_RESOLUTIONS) as VideoResolution[]).map((r) => (
                  <option key={r} value={r}>
                    {r} ({VIDEO_RESOLUTIONS[r].width}×{VIDEO_RESOLUTIONS[r].height})
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Frame rate
              <select className={inputClass} value={fps} onChange={(e) => setFps(Number(e.target.value) as VideoFps)} disabled={running} data-testid="video-fps">
                {VIDEO_FPS.map((f) => (
                  <option key={f} value={f}>
                    {f} fps
                  </option>
                ))}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Quality
              <select className={inputClass} value={quality} onChange={(e) => setQuality(e.target.value as VideoQuality)} disabled={running} data-testid="video-quality">
                <option value="standard">Standard</option>
                <option value="high">High</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              Range
              <select className={inputClass} value={useRange ? 'range' : 'full'} onChange={(e) => setUseRange(e.target.value === 'range')} disabled={running} data-testid="video-range">
                <option value="full">Full session ({formatSeconds(sessionSec)})</option>
                <option value="range">Part (seconds)</option>
              </select>
            </label>
            {useRange && (
              <>
                <label className="flex flex-col gap-1 text-xs text-zinc-400">
                  From (s)
                  <input className={inputClass} type="number" min={0} step={0.1} value={start} onChange={(e) => setStart(e.target.value)} disabled={running} data-testid="video-range-start" />
                </label>
                <label className="flex flex-col gap-1 text-xs text-zinc-400">
                  To (s)
                  <input className={inputClass} type="number" min={0} step={0.1} value={end} placeholder={sessionSec.toFixed(2)} onChange={(e) => setEnd(e.target.value)} disabled={running} data-testid="video-range-end" />
                </label>
              </>
            )}
            <label className="flex flex-col gap-1 text-xs text-zinc-400">
              File name
              <input className={inputClass} value={name} maxLength={100} onChange={(e) => setName(e.target.value)} disabled={running} data-testid="video-filename" />
            </label>
            <label className="flex items-center gap-2 self-end text-xs text-zinc-300">
              <input type="checkbox" checked={includeAudio} onChange={(e) => setIncludeAudio(e.target.checked)} disabled={running} data-testid="video-audio" />
              Include audio (music and clips)
            </label>
          </div>
          <p className="text-xs text-zinc-400" data-testid="video-estimate" data-frames={estimate.frames} data-bytes={estimate.bytes}>
            {rangeCheck.ok ? (
              <>
                {formatSeconds(rangeCheck.end - rangeCheck.start)} · {estimate.frames.toLocaleString('en-US')} frames · about {mb(estimate.bytes)} · {fileName}
                {!includeAudio && ' · no audio track'}
                {session.muted && includeAudio && ' · the preview is muted, but the export includes the music'}
              </>
            ) : (
              <span className="text-red-300" data-testid="video-range-error">{rangeCheck.error}</span>
            )}
          </p>
          {tooBig && <Alert title={`About ${mb(estimate.bytes)}: over the ${mb(MAX_VIDEO_BYTES)} limit`}>Choose a lower resolution or quality, or a shorter range.</Alert>}
          {estimate.expensive.length > 0 && !tooBig && (
            <ul className="list-disc pl-5 text-xs text-amber-300" data-testid="video-warnings">
              {estimate.expensive.map((w) => (
                <li key={w}>{w}</li>
              ))}
            </ul>
          )}
          {method === 'realtime' && (
            <p className="text-xs text-amber-300" data-testid="video-realtime-note">
              Real-time recording takes as long as the video and needs this tab visible. Frames are captured as the browser draws them, so slow machines may drop frames; the result reports them.
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="primary" onClick={startExport} disabled={!canStart} data-testid="video-start">
              Export video
            </Button>
            {running && (
              <Button onClick={() => videoExporter.cancel()} data-testid="video-cancel">
                Cancel
              </Button>
            )}
            {state.status === 'done' && state.url && (
              <>
                <a className="text-sm text-sky-300 underline" href={state.url} download={state.fileName} data-testid="video-download">
                  Download {state.fileName}
                </a>
                <Button size="sm" variant="ghost" onClick={() => videoExporter.reset()}>
                  Clear
                </Button>
              </>
            )}
          </div>
        </>
      )}
      <ExportStatus />
    </div>
  );
}

function ExportStatus() {
  const { videoExporter } = useServices();
  const s = useObservable(videoExporter.state);
  if (s.status === 'idle') return null;
  const pct = Math.round(s.progress * 100);
  return (
    <div className="flex flex-col gap-1 text-xs text-zinc-300" data-testid="video-status" data-status={s.status} data-phase={s.phase ?? ''} data-method={s.method ?? ''}>
      {s.status === 'running' && (
        <>
          <div className="flex justify-between">
            <span>
              {s.phase ? PHASE_LABELS[s.phase] : 'Working'}
              {s.method === 'realtime' ? ' (real-time recording)' : ''}
            </span>
            <span>
              {s.framesTotal > 0 && `${s.framesDone}/${s.framesTotal} frames · `}
              {s.method === 'realtime' && s.elapsedSec !== undefined ? `${formatSeconds(s.elapsedSec)} recorded, ${formatSeconds(s.remainingSec ?? 0)} left · ` : ''}
              {pct}%
            </span>
          </div>
          <div className="h-2 overflow-hidden rounded bg-zinc-800" role="progressbar" aria-label="Video export progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
            <div className="h-full bg-sky-500 transition-[width]" style={{ width: `${pct}%` }} />
          </div>
        </>
      )}
      {s.status === 'done' && s.report && (
        <p data-testid="video-summary">
          Exported {s.fileName} · {s.report.width}×{s.report.height} · {s.report.fps} fps · {formatSeconds(s.report.durationSec)} · {s.report.frames} frames · {mb(s.report.bytes)} ·{' '}
          {s.report.audio.included ? 'with audio' : 'no audio'} · {METHOD_LABELS[s.report.method]} · {(s.report.timings.totalMs / 1000).toFixed(1)} s
          {s.report.realtime && ` · ${s.report.realtime.droppedFrames} of ${s.report.frames} frames dropped (longest gap ${s.report.realtime.maxGapMs} ms)`}
          {s.report.realtime?.keptReason && (
            <span data-testid="video-kept-raw">
              {' '}
              · kept as recorded ({s.report.realtime.keptReason === 'too-large' ? 'too large to add a duration and seek index' : 'the recording could not be re-indexed'}): players may show an unknown length and seek slowly
            </span>
          )}
        </p>
      )}
      {s.status === 'cancelled' && <p data-testid="video-cancelled">Export cancelled. No file was produced.</p>}
      {s.status === 'error' && <Alert title="Video export failed">{s.error}</Alert>}
    </div>
  );
}

/** Probe grid: frames are compared as 64×36 averages (codec noise averages out, motion does not). */
export const PROBE_W = 64;
export const PROBE_H = 36;

async function probeFrames(project: Project, sessionId: string, seconds: number[], width: number, height: number, loadImage: (id: string) => Promise<ImageBitmap>, features: AudioFeatures | null) {
  const renderer = await SessionFrameRenderer.create(takeSnapshot(project, sessionId), width, height, { loadImage, features });
  try {
    const out: { seconds: number; rgba: number[] }[] = [];
    const small = document.createElement('canvas');
    small.width = PROBE_W;
    small.height = PROBE_H;
    const g = small.getContext('2d', { willReadFrequently: true })!;
    g.imageSmoothingQuality = 'high';
    for (const t of seconds) {
      renderer.renderFrame(t);
      g.drawImage(renderer.canvas, 0, 0, PROBE_W, PROBE_H);
      out.push({ seconds: t, rgba: Array.from(g.getImageData(0, 0, PROBE_W, PROBE_H).data) });
    }
    return out;
  } finally {
    renderer.destroy();
  }
}

function withRenderer<T>(r: SessionFrameRenderer, work: (r: SessionFrameRenderer) => T): T {
  try {
    return work(r);
  } finally {
    r.destroy();
  }
}

/** One export frame drawn and read back (a 1-pixel copy forces the GPU to finish the frame). */
function timedFrame(r: SessionFrameRenderer, t: number): number {
  const sink = document.createElement('canvas');
  sink.width = sink.height = 1;
  const g = sink.getContext('2d', { willReadFrequently: true })!;
  const t0 = performance.now();
  r.renderFrame(t);
  g.drawImage(r.canvas, 0, 0, 1, 1, 0, 0, 1, 1);
  g.getImageData(0, 0, 1, 1);
  return performance.now() - t0;
}

async function probeAudio(project: Project, session: AvSession, start: number, seconds: number, audio: RenderAudio) {
  const rendered = await renderSessionAudio(project, session, start, Math.round(seconds * AUDIO_SAMPLE_RATE), AUDIO_SAMPLE_RATE, { audio });
  const ch = rendered.buffer.getChannelData(0);
  const win = AUDIO_SAMPLE_RATE / 1000;
  const envelope: number[] = [];
  for (let i = 0; i + win <= ch.length; i += win) {
    let s = 0;
    for (let j = i; j < i + win; j++) s += ch[j]! * ch[j]!;
    envelope.push(Math.sqrt(s / win));
  }
  return envelope;
}
